import type { SponsoredBrandAsset } from "@adpilot/shared";
import { IconPhoto, IconSearch, IconVideo } from "@tabler/icons-react";
import { format } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { formatAssetSize } from "./sponsored-brands-format";

export function AssetSelect({
  label,
  assets,
  value,
  onValueChange,
  required = false,
  error,
}: {
  label: string;
  assets: SponsoredBrandAsset[];
  value: string | null;
  onValueChange: (value: string | null) => void;
  required?: boolean;
  error?: string;
}) {
  const { t } = useTranslation("campaignCreation");
  const [open, setOpen] = useState(false);
  const [draftValue, setDraftValue] = useState<string | null>(value);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<"newest" | "oldest">("newest");
  const visibleAssets = [...assets]
    .filter((asset) => {
      const normalizedQuery = query.trim().toLocaleLowerCase();
      if (!normalizedQuery) return true;
      return [asset.name, asset.assetId, ...asset.subtypes, ...asset.searchTerms].some(
        (candidate) => candidate.toLocaleLowerCase().includes(normalizedQuery),
      );
    })
    .sort((left, right) => {
      const leftUpdatedAt = Date.parse(left.lastUpdatedAt ?? "") || 0;
      const rightUpdatedAt = Date.parse(right.lastUpdatedAt ?? "") || 0;
      return sort === "newest" ? rightUpdatedAt - leftUpdatedAt : leftUpdatedAt - rightUpdatedAt;
    });
  const selectedAsset = assets.find((asset) => asset.assetId === value) ?? null;

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (nextOpen) {
      setDraftValue(value);
      setQuery("");
      setSort("newest");
    }
  }

  return (
    <Field data-invalid={error ? true : undefined}>
      <FieldLabel>
        {label}
        {required ? " *" : null}
      </FieldLabel>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <Button
          className="w-full justify-start font-normal"
          aria-invalid={error ? true : undefined}
          disabled={assets.length === 0}
          onClick={() => handleOpenChange(true)}
          type="button"
          variant="outline"
        >
          <IconPhoto />
          {selectedAsset?.name ?? t("sb.creative.chooseAssets")}
        </Button>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t("sb.creative.assetLibrary.title")}</DialogTitle>
          </DialogHeader>
          <DialogPanel className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-[1fr_13rem]">
              <InputGroup>
                <InputGroupAddon>
                  <IconSearch />
                </InputGroupAddon>
                <InputGroupInput
                  aria-label={t("sb.creative.assetLibrary.search")}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t("sb.creative.assetLibrary.search")}
                  value={query}
                />
              </InputGroup>
              <Select
                items={[
                  { value: "newest", label: t("sb.creative.assetLibrary.newest") },
                  { value: "oldest", label: t("sb.creative.assetLibrary.oldest") },
                ]}
                onValueChange={(nextValue) => {
                  if (nextValue === "newest" || nextValue === "oldest") setSort(nextValue);
                }}
                value={sort}
              >
                <SelectTrigger aria-label={t("sb.creative.assetLibrary.sortLabel")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t("sb.creative.assetLibrary.newest")}</SelectItem>
                  <SelectItem value="oldest">{t("sb.creative.assetLibrary.oldest")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {t("sb.creative.assetLibrary.filterLabel")}
              </span>
              <span className="rounded-full bg-muted px-3 py-1">
                {t(
                  assets.length > 0 &&
                    assets.every((asset) =>
                      asset.subtypes.some((subtype) => subtype.toUpperCase() === "LOGO"),
                    )
                    ? "sb.creative.assetLibrary.logo"
                    : assets[0]?.type === "VIDEO"
                      ? "sb.creative.assetLibrary.video"
                      : "sb.creative.assetLibrary.image",
                )}
              </span>
            </div>
            {visibleAssets.length > 0 ? (
              <div className="grid max-h-[28rem] grid-cols-2 gap-3 overflow-y-auto md:grid-cols-3">
                {visibleAssets.map((asset) => {
                  const isSelected = draftValue === asset.assetId;
                  return (
                    <button
                      aria-pressed={isSelected}
                      className={cn(
                        "overflow-hidden rounded-lg border bg-card text-left transition-colors hover:border-primary/60",
                        isSelected && "border-primary ring-2 ring-primary/20",
                      )}
                      key={asset.assetId}
                      onClick={() => setDraftValue(asset.assetId)}
                      type="button"
                    >
                      <div className="flex aspect-video items-center justify-center bg-muted">
                        {asset.url ? (
                          <img
                            alt=""
                            className="size-full object-contain"
                            loading="lazy"
                            src={asset.url}
                          />
                        ) : asset.type === "VIDEO" ? (
                          <IconVideo className="size-10 text-muted-foreground" />
                        ) : (
                          <IconPhoto className="size-10 text-muted-foreground" />
                        )}
                      </div>
                      <div className="space-y-1 p-3">
                        <p className="truncate font-medium">{asset.name}</p>
                        <p className="text-muted-foreground text-xs">
                          {formatAssetSize(asset.sizeBytes)} · {asset.width}×{asset.height}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {asset.subtypes.join(", ") || asset.type}
                        </p>
                        {asset.lastUpdatedAt ? (
                          <p className="text-muted-foreground text-xs">
                            {format(new Date(asset.lastUpdatedAt), "yyyy-MM-dd HH:mm:ss")}
                          </p>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="py-12 text-center text-muted-foreground">
                {t("sb.creative.assetLibrary.noResults")}
              </p>
            )}
          </DialogPanel>
          <DialogFooter>
            <Button onClick={() => setOpen(false)} type="button" variant="outline">
              {t("actions.cancel")}
            </Button>
            <Button
              disabled={!draftValue}
              onClick={() => {
                onValueChange(draftValue);
                setOpen(false);
              }}
              type="button"
            >
              {t("sb.creative.assetLibrary.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {assets.length === 0 ? (
        <FieldDescription>{t("sb.creative.noEligibleAssets")}</FieldDescription>
      ) : null}
      {error ? <FieldError>{error}</FieldError> : null}
    </Field>
  );
}
